import { css } from '@emotion/react';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { ButtonComponent } from '~/_shared/baseComponents/buttons';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { noop } from '~/_shared/utils/function.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import type { BoundaryGroup } from '~/boundary/boundary.types';
import { BoundaryLabelSettingsComponent } from '~/boundary/settings/boundaryLabels/boundaryLabelSettings.component';
import { BoundarySettingsBoundaryPreviewComponent } from '~/boundary/settings/common/boundarySettingsBoundaryPreview';
import { CommonSettingsContainer } from '~/boundary/settings/common/commonSettingsContainer';
import type { ModalProps } from '~/modal/modalType.enum';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';

const modalContentStyle = css({
  padding: 0,
});

const boundaryGroupSettingsContainerStyle = css({
  width: '100%',
  paddingTop: 20,
  paddingBottom: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  flexShrink: 0,
  flexGrow: 1,

  '&, & *': {
    boxSizing: 'border-box',
  },
});

const previewAndCheckboxesContainerStyles = css({
  alignItems: 'flex-start',
  display: 'flex',
  gap: 20,
  paddingInline: 24,
});

export const previewContainerStyle = css({
  borderRadius: 4,
  flex: '1',
});

export const DataLayerStyleEditModalComponent = ({
  isOpen,
  isSubmitDisabled,
  boundaryTerritoryGroup,
  boundaryGroup,
  onBoundaryTerritoryGroupChange,
  onClose,
  onSubmitClick,
}: ModalProps<{
  isSubmitDisabled?: boolean;
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
  boundaryGroup: BoundaryGroup;
  onBoundaryTerritoryGroupChange: (territoryGroup: BoundaryTerritoryGroup) => void;
  onSubmitClick: () => void;
}>) => {
  const [t] = useTranslation();

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      caption={t('Fill Settings')}
      contentStyle={modalContentStyle}
      confirmButton={(
        <ButtonComponent
          prefixIcon={faCheck}
          text={t('Save')}
          onClick={onSubmitClick}
          isDisabled={isSubmitDisabled}
        />
      )}
    >
      <div css={boundaryGroupSettingsContainerStyle}>
        <div css={previewAndCheckboxesContainerStyles}>
          <BoundarySettingsBoundaryPreviewComponent
            css={previewContainerStyle}
            squareSize={226}
            boundaryTerritoryGroup={boundaryTerritoryGroup}
          />
          <CommonSettingsContainer
            boundaryTerritoryGroup={boundaryTerritoryGroup}
            onBoundaryTerritoryGroupChange={onBoundaryTerritoryGroupChange}
          />
        </div>
        <BoundaryLabelSettingsComponent
          disallowCustomValue
          boundaryGroup={boundaryGroup}
          boundaryTerritoryGroup={boundaryTerritoryGroup}
          onBoundaryTerritoryGroupChange={onBoundaryTerritoryGroupChange}
          setIsFormValid={noop}
          setIsLoading={noop}
        />
      </div>
    </ModalComponent>
  );
};
